// Type-only import: erased at build time, so this module carries NO runtime
// dependency on the design system (or on React). That is deliberate — the badge
// rule is pure data and the node-only vitest tree can exercise it directly.
import type { CanonicalStatus } from "@ardept/design-system";
import type { OperatorJobDto } from "../api/types.js";

/**
 * Phase 499 (card #1832) AC-2/AC-5 — the TS mirror of the engine's CLOSED derived
 * job-status vocabulary. Authoritative source:
 * `platform/core/JobStore/OperatorJobStatus.cs` (the `const string` members); this
 * array is fenced to SET EQUALITY with it (see
 * docs/architecture/CLOSED-VOCABULARIES.md). Add a member on one side only and the
 * fence goes red.
 *
 * These are the values `?status=` accepts (an off-vocabulary value is a 400
 * `invalid_status`, InternalApiEndpoints.cs), which is why the Jobs screen builds
 * its Status dropdown from this array rather than from the badge kit.
 */
export const JOB_STATUS_VALUES = [
  "failed",
  "cancelled",
  "complete",
  "awaiting",
  "in_progress",
] as const;

/** One member of the engine's derived status vocabulary. */
export type JobStatus = (typeof JOB_STATUS_VALUES)[number];

/**
 * The console's one-line rendering choice: engine status -> the badge the design
 * kit already ships (`STATUS_MAP`, packages/design-system/src/StatusBadge.tsx).
 *
 * TOTAL over `JobStatus` by construction — the `Record<JobStatus, …>` annotation is
 * what makes a new engine member a compile error here rather than a silent miss at
 * runtime. Four arms are identity; `awaiting` deliberately reuses the existing
 * `pending_approval` badge ("Pending approval", `sbadge--pending`) rather than
 * introducing a new `sbadge--*` class or `--status-*` token. The engine's wire
 * vocabulary stays platform-honest (`awaiting`); the mapping onto the visual
 * language lives here, in the consumer, because the kit under
 * the AR Dept. Design System is the visual source of truth (CLAUDE.md).
 */
export const ENGINE_STATUS_BADGE: Record<JobStatus, CanonicalStatus> = {
  failed: "failed",
  cancelled: "cancelled",
  complete: "complete",
  awaiting: "pending_approval",
  in_progress: "in_progress",
};

/**
 * What the badge needs off a job row. Every field's TYPE is read from
 * `OperatorJobDto` rather than restated, so the wire shape cannot drift away from
 * the rule that reads it.
 *
 * `status` is relaxed to optional on purpose: a console build can be served a page
 * from an engine that predates Phase 499, and "the field isn't there" must land in
 * the quiet arm, not the loud one.
 */
export type JobBadgeInput =
  Pick<OperatorJobDto, "state" | "fail_reason"> &
  Partial<Pick<OperatorJobDto, "status">>;

function isJobStatus(value: string | undefined): value is JobStatus {
  return value !== undefined && (JOB_STATUS_VALUES as readonly string[]).includes(value);
}

/**
 * Phase 499 AC-2 — the badge a job row renders. TOTAL, with `in_progress` as the
 * ONLY fallback.
 *
 * Arm order is load-bearing (truth beats nicety), and mirrors
 * `OperatorJobStatus.SqlExpression`'s own ordering:
 *
 *   1. `fail_reason != null` — the canonical "this job failed" signal (Phase 19
 *      D-FAIL-01), the same one the JobDetail failure card and the Failures screen
 *      key on. Checked FIRST so a failed job can never be dressed up as complete.
 *   2. the engine's derived `status`.
 *
 * No workflow is special. Card #3357 removed the last bespoke table (the
 * hello-world demo's state literals, gated on its workflow id): every workflow's
 * badge — the demo's included — reads the engine's status.
 *
 * There is deliberately NO `failed` fallback. The retired `mapStateToStatus()`
 * returned `failed` for any state literal it did not recognise "so operators
 * notice" — which meant every real workflow's states (DONE, SWEEP, AWAIT_CONFIRM,
 * LISTENING, …) rendered FAILED for five months. An unrecognised or absent `status`
 * now reads `in_progress`: quiet, and true.
 */
export function jobBadgeStatus(job: JobBadgeInput): CanonicalStatus {
  if (job.fail_reason != null) return "failed";

  return isJobStatus(job.status) ? ENGINE_STATUS_BADGE[job.status] : "in_progress";
}

/**
 * Phase 15 Plan 04 — engine literal for "operator approval pending". Used by
 * JobDetailScreen's Approve/Reject visibility gate. The CORRECT comparison is
 * `job.state === ENGINE_STATE_PENDING_REVIEW`; comparing against the SPA UI
 * label (`pending_approval`) silently always returns false because job.state
 * is the UPPERCASE engine literal.
 */
export const ENGINE_STATE_PENDING_REVIEW = "PENDING_REVIEW" as const;
